import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Facebook, Instagram, Plane, Shield, Eye, Star, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const TravelAgencyLanding = () => {
  const [activeSlogan, setActiveSlogan] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  const slogans = [
    "Podróże, na które Cię stać. Jakość, której oczekujesz.",
    "Biuro podróży z tradycyjnymi zasadami.",
    "Doradzamy szczerze.",
    "Zobacz wakacje, zanim w nie wyruszysz."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlogan((prev) => (prev + 1) % slogans.length);
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50 transition-all duration-300">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_17c43a45-7441-4362-9e4c-0270a314e88c/artifacts/m9hojx8k_Gemini_Generated_Image_86kw3r86kw3r86kw.png" 
              alt="Wakacyjna Przystań Logo" 
              className="h-12 w-12 object-contain"
            />
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Wakacyjna Przystań</h1>
              <p className="text-xs text-slate-600">Biuro Podróży Skierniewice</p>
            </div>
          </div>
          <nav className="hidden md:flex space-x-6">
            <a href="#about" className="text-slate-700 hover:text-cyan-600 transition-colors">O nas</a>
            <a href="#services" className="text-slate-700 hover:text-cyan-600 transition-colors">Usługi</a>
            <a href="#offer" className="text-slate-700 hover:text-cyan-600 transition-colors">Oferta</a>
            <a href="#contact" className="text-slate-700 hover:text-cyan-600 transition-colors">Kontakt</a>
          </nav>
          <Button onClick={scrollToContact} className="bg-cyan-600 hover:bg-cyan-700 text-white transition-all duration-300">
            Umów rozmowę
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        id="hero" 
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.6), rgba(15, 23, 42, 0.4)), url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxjYWxtJTIwYmVhY2h8ZW58MHx8fHwxNzcwNzE3Njk5fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="container mx-auto px-4 text-center z-10">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">
            Twoje bezpieczne wakacje
          </h2>
          <p className="text-xl md:text-2xl text-white/95 max-w-4xl mx-auto mb-10 leading-relaxed">
            W świecie szybkich rezerwacji i automatycznych infolinii wybieramy inną drogę.
            <br />
            W Wakacyjnej Przystani najważniejsze są rozmowa, bezpieczeństwo i spokój klienta.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={scrollToContact}
              size="lg" 
              className="bg-cyan-600 hover:bg-cyan-700 text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              Umów rozmowę
            </Button>
            <Button 
              onClick={scrollToContact}
              size="lg" 
              variant="outline" 
              className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-slate-800 text-lg px-8 py-6 shadow-lg transition-all duration-300 hover:scale-105"
            >
              Przyjdź porozmawiać
            </Button>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white" />
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-slate-50" data-animate>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-4xl font-bold text-slate-800 mb-6">Tradycja spotyka nowoczesność</h3>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-slate-800 mb-3">Tradycyjne biuro</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Nowoczesne biuro podróży w Skierniewicach z tradycyjnym podejściem do klienta
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star className="w-8 h-8 text-amber-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-slate-800 mb-3">Uczciwe doradztwo</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Doradzamy szczerze - bez presji sprzedaży, jak przyjaciołom
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plane className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-slate-800 mb-3">Profesjonalizm</h4>
                  <p className="text-slate-600 leading-relaxed">
                    Spokój i pełna opieka nad klientem w każdym etapie podróży
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* See Before You Travel Section */}
      <section id="preview" className="py-20 bg-white" data-animate>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1">
              <img 
                src="https://images.unsplash.com/photo-1611892440504-42a792e24d32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxob3RlbCUyMHJvb218ZW58MHx8fHwxNzcwNzE3NzAzfDA&ixlib=rb-4.1.0&q=85"
                alt="Hotel room preview"
                className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="flex items-center mb-4">
                <Eye className="w-10 h-10 text-cyan-600 mr-3" />
                <h3 className="text-4xl font-bold text-slate-800">Zobacz wakacje, zanim w nie wyruszysz</h3>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                W naszym biurze klienci mogą obejrzeć hotele, pokoje, plaże i okolice na dużym ekranie multimedialnym.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Żadnych "ślepych" rezerwacji. To, co widzisz przed wyjazdem, jest tym, czego doświadczysz na miejscu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Safety and Quality Section */}
      <section id="safety" className="py-20 bg-slate-800 text-white" data-animate>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <h3 className="text-4xl font-bold mb-6">Bezpieczny wybór, spokojna decyzja</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-lg text-slate-200">Starannie wybieramy zweryfikowane oferty od zaufanych touroperatorów</p>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-lg text-slate-200">Oficjalny partner Travel Island</p>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-lg text-slate-200">Skupiamy się na jakości w ramach realnego budżetu klienta</p>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-lg text-slate-200">Usuwamy stres i ryzyko samodzielnej organizacji podróży</p>
                </li>
              </ul>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1475503572774-15a45e5d60b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjB2YWNhdGlvbnxlbnwwfHx8fDE3NzA3MTc3MDh8MA&ixlib=rb-4.1.0&q=85"
                alt="Family vacation"
                className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Travel Comfort Section */}
      <section id="comfort" className="py-20 bg-gradient-to-b from-slate-50 to-white" data-animate>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h3 className="text-4xl font-bold text-slate-800 mb-6">Komfort podróży</h3>
            <p className="text-lg text-slate-600">Spokojny i komfortowy początek Twoich wakacji</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="aspect-video mb-4 rounded-lg overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1530521954074-e64f6810b32d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxhaXJwb3J0JTIwbG91bmdlfGVufDB8fHx8MTc3MDcxNzcxM3ww&ixlib=rb-4.1.0&q=85"
                    alt="Airport service"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-semibold text-slate-800 mb-2">Fast Track</h4>
                <p className="text-slate-600">Usługa Fast Track na lotnisku - bez kolejek i stresu</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="aspect-video mb-4 rounded-lg overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/7746042/pexels-photo-7746042.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Airport lounge"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-semibold text-slate-800 mb-2">Poczekalnie VIP</h4>
                <p className="text-slate-600">Dostęp do poczekalni lotniskowych z nieograniczonym poczęstunkiem</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="aspect-video mb-4 rounded-lg overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1508841220-87fbee1b4767?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxjYWxtJTIwYmVhY2h8ZW58MHx8fHwxNzcwNzE3Njk5fDA&ixlib=rb-4.1.0&q=85"
                    alt="Relaxation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-semibold text-slate-800 mb-2">Relaks</h4>
                <p className="text-slate-600">Spokojny i komfortowy start Twoich wakacji</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Offer Section */}
      <section id="offer" className="py-20 bg-white" data-animate>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-4xl font-bold text-slate-800 mb-12 text-center">Nasza oferta</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "Wczasy i wyjazdy wakacyjne",
                "Bilety lotnicze",
                "Transfery lotniskowe",
                "Wycieczki fakultatywne",
                "Wyjazdy integracyjne dla firm"
              ].map((service, index) => (
                <div 
                  key={index}
                  className="flex items-center p-6 bg-slate-50 rounded-lg hover:bg-cyan-50 transition-all duration-300 hover:shadow-md"
                >
                  <div className="w-3 h-3 bg-cyan-600 rounded-full mr-4"></div>
                  <p className="text-lg text-slate-700 font-medium">{service}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rotating Slogans Section */}
      <section id="slogans" className="py-16 bg-slate-800" data-animate>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="h-24 flex items-center justify-center">
              <p className="text-3xl md:text-4xl font-semibold text-white transition-opacity duration-1000">
                {slogans[activeSlogan]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Office Photos Section */}
      <section id="office" className="py-20 bg-slate-50" data-animate>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-4xl font-bold text-slate-800 mb-12 text-center">Nasze biuro w Skierniewicach</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_17c43a45-7441-4362-9e4c-0270a314e88c/artifacts/ejppqgdy_zdjecie1.jpg"
                alt="Biuro Wakacyjna Przystań"
                className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_17c43a45-7441-4362-9e4c-0270a314e88c/artifacts/zwuxsj9w_zdj%C4%99cie2.jpg"
                alt="Wnętrze biura"
                className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_17c43a45-7441-4362-9e4c-0270a314e88c/artifacts/b6x1dysz_zdjecie5.jpg"
                alt="Punkt obsługi"
                className="rounded-lg shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white" data-animate>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-4xl font-bold text-slate-800 mb-12 text-center">Kontakt</h3>
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-2xl font-semibold text-slate-800 mb-6">Wakacyjna Przystań - Biuro Podróży</h4>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <MapPin className="w-6 h-6 text-cyan-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-lg text-slate-700 font-medium">Aleja Niepodległości 14</p>
                      <p className="text-lg text-slate-700">96-100 Skierniewice</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Phone className="w-6 h-6 text-cyan-600 mr-3 flex-shrink-0" />
                    <a href="tel:504522651" className="text-lg text-slate-700 hover:text-cyan-600 transition-colors">
                      504 522 651
                    </a>
                  </div>
                </div>

                <div className="flex space-x-4 mb-8">
                  <a 
                    href="https://facebook.com/wakacyjnaprzystan" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-cyan-600 hover:text-white rounded-lg transition-all duration-300"
                  >
                    <Facebook className="w-5 h-5" />
                    <span>Facebook</span>
                  </a>
                  <a 
                    href="https://instagram.com/wakacyjna_przystan_skce" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-cyan-600 hover:text-white rounded-lg transition-all duration-300"
                  >
                    <Instagram className="w-5 h-5" />
                    <span>Instagram</span>
                  </a>
                </div>

                <div className="bg-cyan-50 border-l-4 border-cyan-600 p-6 rounded">
                  <p className="text-lg text-slate-700 leading-relaxed">
                    Wpadnij porozmawiać o wakacjach. Doradzimy szczerze - jak przyjaciołom.
                  </p>
                </div>
              </div>

              <div className="h-[400px] rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2464.536247!2d20.145!3d51.956!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDU3JzIxLjYiTiAyMMKwMDgnNDIuMCJF!5e0!3m2!1spl!2spl!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokalizacja Wakacyjna Przystań"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-300">© 2024 Wakacyjna Przystań - Biuro Podróży Skierniewice. Wszystkie prawa zastrzeżone.</p>
        </div>
      </footer>
    </div>
  );
};

export default TravelAgencyLanding;